import React, { useState } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet,
  Alert, Switch, ScrollView, TextInput,
} from 'react-native';
import MapView, { Marker } from 'react-native-maps';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { MapStackParamList } from '../../navigation/AppNavigator';
import { useLandmarksStore } from '../../store';
import { landmarksApi } from '../../api';

type Props = NativeStackScreenProps<MapStackParamList, 'LandmarkDetail'>;

export default function LandmarkDetailScreen({ route, navigation }: Props) {
  const { landmarkId } = route.params;
  const { landmarks, upsertLandmark, removeLandmark } = useLandmarksStore();
  const landmark = landmarks.find((l) => l.landmarkId === landmarkId);

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(landmark?.name ?? '');
  const [description, setDescription] = useState(landmark?.description ?? '');
  const [isPublic, setIsPublic] = useState(landmark?.isPublic ?? true);
  const [lat, setLat] = useState(landmark?.lat ?? 0);
  const [lng, setLng] = useState(landmark?.lng ?? 0);

  if (!landmark) {
    return (
      <View style={styles.container}>
        <Text>Landmark not found.</Text>
      </View>
    );
  }

  async function handleSave() {
    try {
      const updated = await landmarksApi.update(landmarkId, { name, description, isPublic, lat, lng });
      upsertLandmark(updated);
      setEditing(false);
    } catch (e: any) {
      Alert.alert('Failed to update', e.message ?? 'Unknown error');
    }
  }

  function handleCancelEdit() {
    setName(landmark!.name);
    setDescription(landmark!.description ?? '');
    setIsPublic(landmark!.isPublic);
    setLat(landmark!.lat);
    setLng(landmark!.lng);
    setEditing(false);
  }

  function handleMapPress(e: { nativeEvent: { coordinate: { latitude: number; longitude: number } } }) {
    setLat(e.nativeEvent.coordinate.latitude);
    setLng(e.nativeEvent.coordinate.longitude);
  }

  function handleMarkerDragEnd(e: { nativeEvent: { coordinate: { latitude: number; longitude: number } } }) {
    setLat(e.nativeEvent.coordinate.latitude);
    setLng(e.nativeEvent.coordinate.longitude);
  }

  function handleDelete() {
    if (!landmark) return;
    Alert.alert('Delete Landmark', `Delete "${landmark.name}"?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', style: 'destructive', onPress: async () => {
          try {
            await landmarksApi.delete(landmarkId);
            removeLandmark(landmarkId);
            navigation.goBack();
          } catch (e: any) {
            Alert.alert('Failed to delete', e.message ?? 'Unknown error');
          }
        },
      },
    ]);
  }

  return (
    <ScrollView style={styles.container}>
      <MapView
        style={styles.map}
        initialRegion={{ latitude: landmark.lat, longitude: landmark.lng, latitudeDelta: 0.005, longitudeDelta: 0.005 }}
        scrollEnabled={editing}
        zoomEnabled={editing}
        onPress={editing ? handleMapPress : undefined}
      >
        <Marker
          coordinate={{ latitude: lat, longitude: lng }}
          pinColor="#1a73e8"
          draggable={editing}
          onDragEnd={editing ? handleMarkerDragEnd : undefined}
        />
      </MapView>
      {editing && (
        <View style={styles.mapHint} pointerEvents="none">
          <Text style={styles.mapHintText}>Tap map or drag pin to move location</Text>
        </View>
      )}

      <View style={styles.body}>
        {editing ? (
          <>
            <Text style={styles.label}>Name</Text>
            <TextInput style={styles.input} value={name} onChangeText={setName} />
            <Text style={styles.label}>Description</Text>
            <TextInput style={[styles.input, styles.multiline]} value={description} onChangeText={setDescription} multiline />
            <View style={styles.row}>
              <Text>Public</Text>
              <Switch value={isPublic} onValueChange={setIsPublic} />
            </View>
            <Text style={styles.coords}>📍 {lat.toFixed(6)}, {lng.toFixed(6)}</Text>
            <TouchableOpacity style={styles.button} onPress={handleSave}>
              <Text style={styles.buttonText}>Save Changes</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.cancelButton} onPress={handleCancelEdit}>
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <Text style={styles.name}>{landmark.name}</Text>
            {landmark.description ? <Text style={styles.desc}>{landmark.description}</Text> : null}
            <Text style={styles.meta}>{landmark.isPublic ? '🌐 Public' : '🔒 Private'}</Text>
            <Text style={styles.meta}>📍 {landmark.lat.toFixed(6)}, {landmark.lng.toFixed(6)}</Text>
            <Text style={styles.meta}>Added {new Date(landmark.createdAt).toLocaleDateString()}</Text>
            <TouchableOpacity style={styles.button} onPress={() => setEditing(true)}>
              <Text style={styles.buttonText}>Edit</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.deleteButton} onPress={handleDelete}>
              <Text style={styles.deleteText}>Delete Landmark</Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  map: { height: 220, width: '100%' },
  mapHint: { position: 'absolute', top: 185, left: 0, right: 0, alignItems: 'center' },
  mapHintText: {
    backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff',
    fontSize: 12, paddingHorizontal: 12, paddingVertical: 5,
    borderRadius: 12, overflow: 'hidden',
  },
  body: { padding: 20 },
  name: { fontSize: 24, fontWeight: '700', marginBottom: 8 },
  desc: { fontSize: 15, color: '#555', marginBottom: 8 },
  meta: { fontSize: 13, color: '#888', marginBottom: 4 },
  label: { fontSize: 14, fontWeight: '600', color: '#333', marginBottom: 4, marginTop: 12 },
  coords: { color: '#888', fontSize: 12, textAlign: 'center', marginTop: 8 },
  input: { borderWidth: 1, borderColor: '#ccc', borderRadius: 8, padding: 12, fontSize: 16, marginBottom: 12 },
  multiline: { minHeight: 80, textAlignVertical: 'top' },
  row: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  button: { backgroundColor: '#1a73e8', borderRadius: 8, padding: 14, alignItems: 'center', marginTop: 16 },
  buttonText: { color: '#fff', fontWeight: '600', fontSize: 15 },
  cancelButton: { padding: 12, alignItems: 'center' },
  cancelText: { color: '#666', fontSize: 15 },
  deleteButton: { borderWidth: 1, borderColor: '#ea4335', borderRadius: 8, padding: 14, alignItems: 'center', marginTop: 8 },
  deleteText: { color: '#ea4335', fontWeight: '600', fontSize: 15 },
});
